import React, { useState, useCallback, useEffect, useRef } from 'react';
import { SearchIcon, HamburgerIcon } from '@chakra-ui/icons';
import { useColorMode, Box, Flex, Text } from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useThemeToggle } from '../../contexts/ThemeContext';
import { useAuth } from '../../hooks/useAuth';
import { useConversation } from '../../contexts/ConversationContext';
import ThemeAwareLogo from '../common/ThemeAwareLogo';
import './ChatSidebar.css';
import { useChatStore } from '../../stores/chatStore';
import { useSubscription } from '../../contexts/SubscriptionContext';

interface ChatSidebarProps {
  user?: {
    email?: string;
    username?: string;
    subscriptionPlan?: string;
    avatar?: string;
  };
  onLockChange?: (locked: boolean) => void;
  onHoverChange?: (hovered: boolean) => void;
  forceCollapse?: boolean;
  disableHover?: boolean;
}


const ChatSidebar: React.FC<ChatSidebarProps> = ({
  user,
  onLockChange,
  onHoverChange,
  forceCollapse = false,
  disableHover = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { 
    conversations, 
    currentConversation, 
    createConversation, 
    selectConversation, 
    clearCurrentConversation,
    searchConversations,
    isLoading 
  } = useConversation();
  
  // Pull real subscription tier from context; default to Free
  const { subscriptionStatus } = useSubscription();
  const tier = ((subscriptionStatus?.subscription?.tier || subscriptionStatus?.usage_limits?.tier || 'free') as string).toLowerCase();
  const planLabel = `${tier.charAt(0).toUpperCase()}${tier.slice(1)} Plan`;

  const userWithPlan = {
    ...user,
    username: user?.username || user?.email?.split('@')[0] || 'User',
    email: user?.email || 'user@example.com',
    subscriptionPlan: planLabel,
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [isLocked, setIsLocked] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [hoverEnabled, setHoverEnabled] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const profileDropdownRef = useRef<HTMLDivElement>(null);
  const profileSectionRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const hoverZoneRef = useRef<HTMLDivElement>(null);
  const logoSectionRef = useRef<HTMLDivElement>(null);
  const logoContainerRef = useRef<HTMLDivElement>(null);
  const [logoFillRect, setLogoFillRect] = useState<{ left: number; top: number; w: number; h: number }>({ left: 0, top: 0, w: 0, h: 0 });
  const [isLogoHovered, setIsLogoHovered] = useState(false);
  const [isLogoActive, setIsLogoActive] = useState(false);
  
  // Theme functionality
  const { colorMode } = useColorMode();
  const { toggleTheme } = useThemeToggle();
  
  // Notify parent when lock state changes
  useEffect(() => {
    onLockChange?.(isLocked);
  }, [isLocked, onLockChange]);

  // Notify parent when hover state changes
  useEffect(() => {
    onHoverChange?.(isHovered && hoverEnabled && !isLocked);
  }, [isHovered, hoverEnabled, isLocked, onHoverChange]);

  // Track dropdown visibility
  useEffect(() => {
    // Profile dropdown state tracking
  }, [showProfileDropdown]);

  // Apply external controls (split view): force collapse and disable hover
  useEffect(() => {
    if (forceCollapse) {
      if (isLocked) {
        setIsLocked(false);
        onLockChange?.(false);
      }
      if (isHovered) setIsHovered(false);
    }
  }, [forceCollapse]);

  useEffect(() => {
    setHoverEnabled(!disableHover);
    if (disableHover && isHovered) setIsHovered(false);
  }, [disableHover]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileDropdownRef.current && 
        profileSectionRef.current &&
        !profileDropdownRef.current.contains(event.target as Node) &&
        !profileSectionRef.current.contains(event.target as Node)
      ) {
        setShowProfileDropdown(false);
      }
    };

    if (showProfileDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileDropdown]);

  // Handle hover zone mouse events
  useEffect(() => {
    const handleMouseEnter = (event) => {
      // Check if mouse is over logo area and sidebar is collapsed
      const logoContainer = document.querySelector('.fixedLogoContainer');
      let isMouseOverLogo = false;
      
      if (logoContainer) {
        const logoRect = logoContainer.getBoundingClientRect();
        isMouseOverLogo = (
          event.clientX >= logoRect.left && 
          event.clientX <= logoRect.right && 
          event.clientY >= logoRect.top && 
          event.clientY <= logoRect.bottom
        );
      }
      
      if (hoverEnabled) {
        // Only trigger sidebar hover if NOT over logo area when collapsed, OR if already expanded/locked
        if (!isMouseOverLogo || isHovered || isLocked) {
          setIsHovered(true);
        }
      }
    };

    const handleMouseLeave = (event) => {
      setIsHovered(false);
      setShowProfileDropdown(false);
      // Re-enable hover when mouse leaves the sidebar area
      setHoverEnabled(true);
    };

    // Handle mouse movement within hover zone to detect logo area transitions
    const handleMouseMove = (event) => {
      const logoContainer = document.querySelector('.fixedLogoContainer');
      let isMouseOverLogo = false;
      
      if (logoContainer) {
        const logoRect = logoContainer.getBoundingClientRect();
        isMouseOverLogo = (
          event.clientX >= logoRect.left && 
          event.clientX <= logoRect.right && 
          event.clientY >= logoRect.top && 
          event.clientY <= logoRect.bottom
        );
      }
      
      // If mouse moves over logo area while sidebar is collapsed, collapse it
      if (isMouseOverLogo && isHovered && !isLocked && hoverEnabled) {
        setIsHovered(false);
      }
      // If mouse moves away from logo area while sidebar is collapsed, expand it
      else if (!isMouseOverLogo && !isHovered && !isLocked && hoverEnabled) {
        setIsHovered(true);
      }
    };

    const hoverZoneElement = hoverZoneRef.current;
    if (hoverZoneElement) {
      hoverZoneElement.addEventListener('mouseenter', handleMouseEnter);
      hoverZoneElement.addEventListener('mouseleave', handleMouseLeave);
      hoverZoneElement.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      if (hoverZoneElement) {
        hoverZoneElement.removeEventListener('mouseenter', handleMouseEnter);
        hoverZoneElement.removeEventListener('mouseleave', handleMouseLeave);
        hoverZoneElement.removeEventListener('mousemove', handleMouseMove);
      }
    };
  }, [hoverEnabled]);

  // Track logo section rect to size/position the persistent fill exactly
  useEffect(() => {
    const el = logoSectionRef.current;
    const container = logoContainerRef.current;
    if (!el || !container) return;

    const update = () => {
      try {
        const rect = el.getBoundingClientRect();
        const crect = container.getBoundingClientRect();
        const w = Math.ceil(rect.width);
        const h = Math.ceil(rect.height);
        const left = Math.round(crect.left);
        const top = Math.round(crect.top);
        setLogoFillRect((prev) => (
          prev.w === w && prev.h === h && prev.left === left && prev.top === top
            ? prev
            : { w, h, left, top }
        ));
      } catch {}
    };

    update();
    let ro: ResizeObserver | null = null;
    try {
      ro = new ResizeObserver(() => update());
      ro.observe(el);
      ro.observe(container);
    } catch {
      const onResize = () => update();
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }
    return () => {
      try { ro && ro.disconnect(); } catch {}
    };
  }, []);


  const clearAllMessages = useChatStore(state => state.clearAllMessages);

  const handleNewChat = useCallback(async () => {
    // Clear current chat state and navigate to blank /chat
    try {
      clearAllMessages();
    } catch {}
    try {
      // Clear conversation selection so ChatPage renders an empty state
      await Promise.resolve(clearCurrentConversation());
    } catch {}
    navigate('/chat');
  }, [navigate, clearAllMessages, clearCurrentConversation]);

  const isInFreshChat = !currentConversation && location.pathname === '/chat';

  const handleSessionClick = useCallback(async (conversationRef: string) => {
    if (!conversationRef) return;
    
    // Clear any existing selection state before navigating
    // This helps prevent race conditions when switching conversations
    console.log('[ChatSidebar] Navigating to conversation:', conversationRef);
    
    // Use startTransition to mark this as a non-urgent update
    // This helps React prioritize the navigation over other updates
    React.startTransition(() => {
      navigate(`/chat/${conversationRef}`);
    });
  }, [navigate]);

  const filteredSessions = searchQuery 
    ? searchConversations(searchQuery)
    : conversations;

  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) return 'No messages';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className={`sidebarContainer ${isLocked ? 'locked' : ''}`} ref={sidebarRef}>
      {/* Hover zone wrapper - includes toggle and sidebar */}
      <div 
        className={`sidebarHoverZone ${isHovered && hoverEnabled ? 'hovered' : ''}`}
        ref={hoverZoneRef}
      >
        {/* Persistent fill behind logo to prevent text overlap (covered by sidebar on expand) */}
        <div 
          className={`fixedLogoFill${isLogoHovered ? ' hover' : ''}${isLogoActive ? ' active' : ''}`} 
          aria-hidden="true" 
          style={{ 
            width: logoFillRect.w || undefined, 
            height: logoFillRect.h || undefined,
            left: logoFillRect.left || undefined,
            top: logoFillRect.top || undefined,
            display: logoFillRect.w && logoFillRect.h ? 'block' : 'none',
          }}
        />
        {/* Fixed logo section - CONDITIONALLY inside hover zone */}
        <div className="fixedLogoContainer" ref={logoContainerRef}>
          <div 
            className="fixedLogoSection clickable"
            ref={logoSectionRef}
            onMouseEnter={() => {
              setIsLogoHovered(true);
            }}
            onMouseLeave={() => {
              setIsLogoHovered(false);
              setIsLogoActive(false);
            }}
            onMouseDown={() => setIsLogoActive(true)}
            onMouseUp={() => setIsLogoActive(false)}
            onClick={() => navigate('/dashboard')}
            style={{ cursor: 'pointer' }}
            title="LearnYourself - Go to Dashboard"
          >
            <ThemeAwareLogo width={32} height={32} />
            <span className="appName">LearnYourself</span>
          </div>
        </div>
        
        {/* Fixed toggle button */}
        <div className="fixedToggleContainer">
          <button 
            className="toggleBtn"
            onClick={() => {
              const newLocked = !isLocked;
              setIsLocked(newLocked);
              
              // When collapsing (going from locked to unlocked), disable hover temporarily
              if (!newLocked) {
                setHoverEnabled(false);
                setIsHovered(false);
              }
            }}
            title={isLocked ? "Collapse sidebar" : "Lock sidebar open"}
          >
            <HamburgerIcon className={`toggleIcon ${isLocked ? '' : 'rotated'}`} />
          </button>
        </div>
        
        <div className={`sidebar collapsed ${isLocked ? 'locked' : ''} ${isHovered && hoverEnabled && !isLocked ? 'hovered' : ''}`}>
        {/* Header Section */}
        <div className="header">
          <div className="buttonContainer">
            {/* New Chat button - single button that expands */}
            <button className="newChatBtn" onClick={handleNewChat} disabled={isInFreshChat} title={isInFreshChat ? 'Already in a new chat' : undefined}>
              <div className="iconWrapper">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <span className="buttonText">New Chat</span>
            </button>
          </div>
        </div>

        {/* Navigation Section */}
        <div className="navigationWrapper">
          <div className="navigation">
            <div className="navigationContent">
            {/* Search bar */}
            <div className="searchSection">
              <div className="searchInput">
                <SearchIcon className="searchIcon" />
                <input 
                  type="text" 
                  placeholder="Search chats..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            
            {/* Chat History */}
            <div className="historySection">
              <div className="historyHeader">
                <h3 className="historyTitle">Recent</h3>
              </div>
              <div className="scrollContainer">
                <div className="sessionList">
                  {isLoading ? (
                    <div className="sessionItem">
                      <div className="sessionContent">
                        <div className="sessionTitle">Loading conversations...</div>
                      </div>
                    </div>
                  ) : filteredSessions.length > 0 ? (
                    filteredSessions.map(conversation => (
                      <div
                        key={conversation.public_id || conversation.conversation_id}
                        className={`sessionItem ${currentConversation?.conversation_id === conversation.conversation_id ? 'active' : ''}`}
                        onClick={() => handleSessionClick(conversation.public_id)}
                      >
                        <div className="sessionContent">
                          <div className="sessionTitle">{conversation.title || 'Untitled Chat'}</div>
                        </div>
                        <div className="sessionMeta">
                          <span className="sessionTime">
                            {formatTimestamp(conversation.last_message_at || conversation.created_at)}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="sessionEmptyNotice">
                      <div className="sessionTitle">No conversations yet</div>
                      <div className="sessionPreview">Click "New Chat" to start</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>

        {/* Footer Section */}
        <div className="footer">
          {/* User profile */}
          {
            <div 
              ref={profileSectionRef}
              className="userProfileSection"
              onClick={(e) => {
                e.stopPropagation();
                setShowProfileDropdown(!showProfileDropdown);
              }}
            >
              <div className="userAvatar">
                {userWithPlan?.avatar ? (
                  <img src={userWithPlan.avatar} alt="Profile" className="avatarImage" />
                ) : (
                  (() => {
                    const first = (userWithPlan as any)?.first_name?.trim()?.charAt(0) || '';
                    const last = (userWithPlan as any)?.last_name?.trim()?.charAt(0) || '';
                    const uname = (userWithPlan.username || '').trim().charAt(0);
                    const initials = (first && last) ? `${first}${last}` : (first || uname || 'U');
                    return initials.toUpperCase();
                  })()
                )}
              </div>
              <div className="userInfo">
                <div className="userName">
                  {userWithPlan?.username || 'User'}
                </div>
                <div className="userPlanSection">
                  <div className="userPlan">
                    {userWithPlan?.subscriptionPlan || 'Free Plan'}
                  </div>
                  {tier !== 'pro' ? (
                    <button 
                      className="upgradePlanBtn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowProfileDropdown(false);
                        navigate('/pricing');
                      }}
                      title="Upgrade your plan"
                    >
                      <svg width="12" height="12" viewBox="0 -5 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" fill="currentColor"/>
                        <path d="M22 -3L23.2 0.8L27 2L23.2 3.2L22 7L20.8 3.2L17 2L20.8 0.8L22 -3Z" fill="currentColor"/>
                      </svg>
                      <span className="upgradeBtnText">Upgrade</span>
                    </button>
                  ) : (
                    <button
                      className="upgradePlanBtn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowProfileDropdown(false);
                        navigate('/billing');
                      }}
                      title="Manage billing"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19.14 12.94a7.002 7.002 0 0 0 0-1.88l2.03-1.58a.5.5 0 0 0 .12-.65l-1.93-3.34a.5.5 0 0 0-.61-.22l-2.39.96a7.028 7.028 0 0 0-1.63-.95l-.36-2.54A.5.5 0 0 0 13.99 2h-3.98a.5.5 0 0 0-.49.41l-.36 2.54c-.58.23-1.12.54-1.63.95l-2.39-.96a.5.5 0 0 0-.61.22L2.6 8.1a.5.5 0 0 0 .12.65l2.03 1.58c-.05.31-.08.63-.08.95s.03.64.08.95L2.72 13.8a.5.5 0 0 0-.12.65l1.93 3.34c.13.22.39.31.61.22l2.39-.96c.51.41 1.05.72 1.63.95l.36 2.54c.05.24.25.41.49.41h3.98c.24 0 .44-.17.49-.41l.36-2.54c.58-.23 1.12-.54 1.63-.95l2.39.96c.23.09.49 0 .61-.22l1.93-3.34a.5.5 0 0 0-.12-.65l-2.03-1.58ZM12 15.5A3.5 3.5 0 1 1 12 8.5a3.5 3.5 0 0 1 0 7Z" fill="currentColor"/>
                      </svg>
                      <span className="upgradeBtnText">Manage</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          }
          
          
          {/* Profile Dropdown Menu */}
          {showProfileDropdown && (
            <div className="profileDropdown" ref={profileDropdownRef}>
              <div className="dropdownEmail">
                {userWithPlan?.email || 'user@example.com'}
              </div>
              
              <div className="dropdownDivider"></div>
              
              <button 
                className="dropdownItem"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowProfileDropdown(false);
                  navigate('/profile');
                }}
              >
                <svg className="dropdownIcon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                <span>My Profile</span>
              </button>
              
              <button 
                className="dropdownItem"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowProfileDropdown(false);
                  navigate('/documents');
                }}
              >
                <svg className="dropdownIcon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                </svg>
                <span>Documents</span>
              </button>
              
              <button 
                className="dropdownItem"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowProfileDropdown(false);
                  navigate('/billing');
                }}
              >
                <svg className="dropdownIcon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
                </svg>
                <span>Billing & Subscription</span>
              </button>
              
              <div className="dropdownDivider"></div>
              
              <div className="dropdownItem darkModeItem">
                <Box position="relative" display="inline-block">
                  {/* Hidden checkbox for accessibility */}
                  <input
                    type="checkbox"
                    id="darkModeToggle"
                    checked={colorMode === 'dark'}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleTheme();
                    }}
                    style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                    aria-label="Toggle dark mode"
                  />
                  
                  <Flex
                    as="label"
                    htmlFor="darkModeToggle"
                    className="dark-mode-toggle css-txn5bd"
                    cursor="pointer"
                    align="center"
                    position="relative"
                    width="200px"
                    height="40px"
                    borderRadius="20px"
                  bg={colorMode === 'dark' ? 'var(--color-gray-700)' : 'var(--color-gray-300)'}
                    transition="all 0.3s ease"
                    overflow="visible"
                    _hover={{ opacity: 0.85 }}
                    userSelect="none"
                  >
                    {/* Sliding circle/pill */}
                    <Box
                      className="toggle-circle"
                      position="absolute"
                      width="50%"
                      height="36px"
                      bg={colorMode === 'dark' ? 'var(--color-bg-primary, #1a1a1a)' : 'var(--color-bg-primary, white)'}
                      borderRadius="16px"
                      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                      left={colorMode === 'dark' ? 'calc(50% - 2px)' : '2px'}
                      top="2px"
                      boxShadow={colorMode === 'dark' 
                        ? '0 0 8px rgba(255, 255, 255, 0.3), 0 0 16px rgba(255, 255, 255, 0.15), 0 2px 5px rgba(0,0,0,0.1)' 
                        : '0 2px 10px rgba(0,0,0,0.15)'
                      }
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      zIndex="2"
                    >
                      {/* Icon in the circle - centered in button */}
                      {colorMode === 'dark' ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="var(--color-text, white)">
                          <path d="M12 3c.132 0 .263 0 .393 0a7.5 7.5 0 0 0 7.92 12.446a9 9 0 1 1 -8.313 -12.454z" />
                          <path d="M17 4a2 2 0 0 0 2 2a2 2 0 0 0 -2 2a2 2 0 0 0 -2 -2a2 2 0 0 0 2 -2" />
                          <path d="M19 11h2m-1 -1v2" />
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="var(--color-text, black)">
                          <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="2"/>
                          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                      )}
                    </Box>
                    
                    {/* Text labels */}
                    <Flex width="100%" height="100%" position="relative" zIndex={0} pointerEvents="none">
                      {/* Day Mode Text */}
                      <Flex
                        align="center"
                        justify="center"
                        flex={1}
                        color={colorMode !== 'dark' ? 'var(--color-text, black)' : 'var(--color-text-muted, #666)'}
                        fontWeight={colorMode !== 'dark' ? '700' : '400'}
                        transition="all 0.3s ease"
                      >
                        <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.5px">
                          Day
                        </Text>
                      </Flex>

                      {/* Night Mode Text */}
                      <Flex
                        align="center"
                        justify="center"
                        flex={1}
                        color={colorMode === 'dark' ? 'var(--color-text, white)' : 'var(--color-text-muted, #666)'}
                        fontWeight={colorMode === 'dark' ? '700' : '400'}
                        transition="all 0.3s ease"
                      >
                        <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.5px">
                          Night
                        </Text>
                      </Flex>
                    </Flex>
                  </Flex>
                </Box>
              </div>
              
              <div className="dropdownDivider"></div>
              
              <button 
                className="dropdownItem logout"
                onClick={async (e) => {
                  e.stopPropagation();
                  setShowProfileDropdown(false);
                  await logout();
                }}
              >
                <svg className="dropdownIcon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                </svg>
                <span>Log out</span>
              </button>
            </div>
          )}
        </div>
      </div>
      </div> {/* End of sidebarHoverZone */}
    </div>
  );
};

export default ChatSidebar;
